import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import { getInstitutionDetails } from '../../../services/institution';
import { getBranchDetails } from '../../../services/branch';
import { getQueueDetails, reserveTurnInQueue } from '../../../services/queue';
import { addAppointment, getServiceSlots } from '../../../services/appointments';
import CustomCalendar from '../../../components/Calendar/CustomCalendar';
import LoginModal from '../../../components/Modals/LoginModal/LoginModal';
import { useAuth } from '../../../context/AuthContext';
import { connectToSocket, emitSocketEvent, onSocketEvent } from '../../../services/socket';
import { FaFacebook, FaGlobe, FaInstagram, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';

export default function InstitutionDetailsPage() {
    const { institutionID } = useParams();
    const { user, token, setInstLogoData } = useAuth();
    const [institution, setInstitution] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState('');
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [queueInfo, setQueueInfo] = useState(null);
    const [isReserving, setIsReserving] = useState(false);
    const [slots, setSlots] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isBooking, setIsBooking] = useState(false);
    const [snackbarType, setSnackbarType] = useState('success');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchInstitution = async () => {
            try {
                const res = await getInstitutionDetails(institutionID);
                setInstitution(res.data);
                console.log('Institution data:', res.data);
                setInstLogoData(res.data.image);
                setBranches(res.data.Branches || []);
            } catch (err) {
                console.error(err);
                navigate("/not-found");
            } finally {
                setLoading(false);
            }
        };
        fetchInstitution();
    }, [institutionID]);

    useEffect(() => {
        const fetchServices = async () => {
            if (!selectedBranch) return;
            setServices([]);
            setSelectedService(null);
            setServicesLoading(true);
            try {
                const res = await getBranchDetails(selectedBranch);
                setServices(res.data.branch.services || []);
            } catch (err) {
                console.error(err);
            } finally {
                setServicesLoading(false);
            }
        };
        fetchServices();
    }, [selectedBranch]);

    useEffect(() => {
        if (!selectedService) return;

        if (selectedService.reservationType === 'queue') {
            connectToSocket();
            const id = selectedService.queue?.id || selectedService.id;
            emitSocketEvent('join-queue-room', { queueId: id });
            getQueueDetails(id).then(res => setQueueInfo(res.data));
            onSocketEvent('queue:stats:update', (data) => setQueueInfo(data));
        } else {
            getServiceSlots(selectedService.id, selectedBranch).then(res => setSlots(res.data));
        }
    }, [selectedService, selectedBranch]);

    const localISODate = (date) => {
        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date - tzOffset).toISOString().split("T")[0];
    };

    const handleReserve = async () => {
        if (!user) {
            setLoginModalOpen(true);
            return;
        }

        if (user.role === 'admin' || user.role === 'employee') {
            setSnackbarMessage('الموظفون والمدراء لا يمكنهم الحجز.');
            setSnackbarType('error');
            setSnackbarOpen(true);
            return;
        }

        setIsReserving(true);
        try {
            const res = await reserveTurnInQueue(token, selectedService.queue?.id || selectedService.id);
            window.location.href = res.data.reservationUrl;
        } catch {
            setSnackbarMessage('لديك حجز مسبق أو فشل الحجز');
            setSnackbarType('error');
            setSnackbarOpen(true);
        } finally {
            setIsReserving(false);
        }
    };

    const handleConfirmBooking = async () => {
        if (!selectedSlot || !selectedDate) return;
        if (!user) return setLoginModalOpen(true);

        if (user.role === 'admin' || user.role === 'employee') {
            setSnackbarMessage('الموظفون والمدراء لا يمكنهم الحجز.');
            setSnackbarType('error');
            setSnackbarOpen(true);
            return;
        }

        setIsBooking(true);
        try {
            await addAppointment(token, {
                serviceId: selectedService.id,
                branchId: selectedBranch,
                appointmentDate: selectedDate,
                startTime: selectedSlot
            });
            setSnackbarMessage('تم تأكيد الحجز بنجاح');
            setSnackbarType('success');
        } catch {
            setSnackbarMessage('فشل تأكيد الحجز. حاول مرة أخرى.');
            setSnackbarType('error');
        } finally {
            setSnackbarOpen(true);
            setIsBooking(false);
        }
    };

    const getIcon = (label) => {
        const lower = label?.toLowerCase() || '';
        if (lower.includes('whatsapp')) return <FaWhatsapp />;
        if (lower.includes('facebook')) return <FaFacebook />;
        if (lower.includes('phone')) return <FaPhoneAlt />;
        if (lower.includes('instagram')) return <FaInstagram />;
        return <FaGlobe />;
    };

    return (
        <div className='page'>
            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {institution && (
                        <div style={styles.institutionHeader}>
                            <img src={institution.image || '/assets/default.jpg'} alt="institution" style={styles.institutionImage} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginLeft: '20px' }}>
                                <h2>{institution.name}</h2>
                                <p>{branches[0]?.name} - {branches[0]?.city} - {branches[0]?.street}</p>
                                </div>
                                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                    {institution.contactInfo?.map((info, idx) => (
                                        <a key={idx} href={info.url} target="_blank" rel="noreferrer" style={styles.contactLink}>
                                            {getIcon(info.label)}
                                        </a>
                                    ))}
                                </div>
                        </div>
                    )}

                    <div style={styles.formGroup}>
                        <label>اختر الفرع:</label>
                        <select style={styles.select} value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}>
                            <option value="">اختر فرعًا</option>
                            {branches.map(branch => (
                                <option key={branch.id} value={branch.id}>{branch.name}</option>
                            ))}
                        </select>
                    </div>

                    {selectedBranch && (
                        <div>
                            <h3>اختر الخدمة:</h3>
                            {servicesLoading ? (
                                <div style={{ textAlign: 'center', margin: '20px 0' }}>
                                    <CircularProgress size={30} />
                                </div>
                            ) : services.length > 0 ? (
                                <div style={styles.serviceList}>
                                    {services.map(service => (
                                        <div
                                            key={service.id}
                                            onClick={() => setSelectedService(service)}
                                            style={{
                                                ...styles.serviceCard,
                                                ...(selectedService?.id === service.id ? styles.selectedServiceCard : {})
                                            }}
                                        >
                                            <h4>{service.name}</h4>
                                            <p>⏱ المدة: {service.servicePeriod} دقيقة</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ color: '#777' }}>لا توجد خدمات لهذا الفرع.</p>
                            )}
                        </div>
                    )}

                    {selectedService && (
                        <div style={styles.actionCard}>
                            {selectedService.reservationType === 'queue' ? (
                                <>
                                    <h3>{selectedService.name}</h3>
                                    <p>🕒 الانتظار المتوقع: <strong>{queueInfo?.estimatedWaitingTime || '--'}</strong> دقيقة</p>
                                    <p>👥 في الطابور: <strong>{queueInfo?.waitingCount || 0}</strong></p>

                                    {user && (user.role === 'admin' || user.role === 'employee') ? (
                                        <p style={{ color: 'gray', marginTop: '10px' }}>لا يمكنك الحجز كمدير أو موظف</p>
                                    ) : (
                                        <>
                                            <button
                                                onClick={handleReserve}
                                                disabled={isReserving || institution?.isOpen !== true}
                                                style={{
                                                    ...styles.button,
                                                    backgroundColor: institution?.isOpen !== true ? '#ccc' : styles.button.backgroundColor,
                                                    cursor: institution?.isOpen !== true ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isReserving ? 'جاري الحجز...' : 'أخذ دور الآن'}
                                            </button>
                                            {institution?.isOpen !==  true&& (
                                                <p style={{ color: 'red', marginTop: '5px', fontSize: '14px' }}>
                                                    المؤسسة مغلقة حاليًا ولا يمكن الحجز الآن
                                                </p>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div style={styles.calendarSlots}>
                                        <CustomCalendar
                                            value={selectedDate ? new Date(selectedDate) : null}
                                            onChange={(date) => setSelectedDate(localISODate(date))}
                                            tileDisabled={({ date }) => !slots.some(s => s.date === localISODate(date))}
                                        />
                                        <div style={styles.slots}>
                                            {selectedDate && slots.find(s => s.date === selectedDate) ? (
                                                slots.find(s => s.date === selectedDate).slots.map((slot, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setSelectedSlot(slot.start)}
                                                        style={{
                                                            ...styles.slotButton,
                                                            ...(selectedSlot === slot.start ? styles.selectedSlotButton : {})
                                                        }}
                                                    >
                                                        {slot.start} - {slot.end}
                                                    </button>
                                                ))
                                            ) : <p>اختر يومًا لعرض المواعيد</p>}
                                        </div>
                                    </div>

                                    {user && (user.role === 'admin' || user.role === 'employee') ? (
                                        <p style={{ color: 'gray', marginTop: '10px' }}>لا يمكنك الحجز كمدير أو موظف</p>
                                    ) : (
                                        <button onClick={handleConfirmBooking} disabled={!selectedSlot} style={styles.button}>
                                            حجز موعد مسبق
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </>
            )}

            {loginModalOpen && (
                <LoginModal
                    onLoginSuccess={() => {
                        setLoginModalOpen(false);
                        handleReserve();
                    }}
                    onClose={() => setLoginModalOpen(false)}
                />
            )}

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarType} variant="filled">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

const styles = {
    institutionHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
        alignItems: 'center'
    },
    institutionImage: {
        width: '100px',
        height: '100px',
        borderRadius: '8px',
        objectFit: 'cover'
    },
    tag: {
        backgroundColor: '#e6f9f7',
        color: '#01c8aa',
        padding: '5px 10px',
        borderRadius: '5px',
        fontSize: '13px'
    },
    workingHours: {
        marginTop: '10px',
        fontSize: '14px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center'
    },
    formGroup: { margin: '20px 0' },
    select: {
        width: '100%',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #ccc',
        marginTop: '10px'
    },
    serviceList: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '20px'
    },
    serviceCard: {
        backgroundColor: 'white',
        border: '1px solid #ccc',
        padding: '15px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.3s'
    },
    selectedServiceCard: {
        backgroundColor: '#effbfb',
        borderColor: '#01c8aa',
        boxShadow: '0 2px 10px rgba(1, 200, 170, 0.2)'
    },
    actionCard: {
        marginTop: '30px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)'
    },
    button: {
        marginTop: '15px',
        width: '100%',
        padding: '12px',
        backgroundColor: '#01c8aa',
        color: 'white',
        fontSize: '16px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer'
    },
    calendarSlots: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '25px',
        marginTop: '20px'
    },
    slots: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
        marginTop: '10px',
        flex: '1',
        maxWidth: '400px'
    },
    slotButton: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '6px',
        cursor: 'pointer',
        backgroundColor: '#f8f8f8',
        textAlign: 'center'
    },
    selectedSlotButton: {
        backgroundColor: '#01c8aa',
        color: 'white',
        borderColor: '#01c8aa'
    },
    contactLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: '#f0f0f0',
        padding: '6px 12px',
        borderRadius: '6px',
        color: '#333',
        textDecoration: 'none',
        fontSize: '14px'
    }
};
